import {
    createRxDatabase,
    addRxPlugin,
    type RxDatabase,
    type RxCollection
} from 'rxdb';
import { getRxStorageDexie } from 'rxdb/plugins/storage-dexie';
import { RxDBMigrationSchemaPlugin } from 'rxdb/plugins/migration-schema';
import { installAuthorJournal } from './authorJournal';
import {
    clearOpenAbandoned,
    currentDatabaseName,
    markOpenAbandoned
} from './openStall';
import { injectedDatabaseFault } from '../features/support/devFaults';
import {
    projectEntity,
    groupEntity,
    userEntity,
    userProfileEntity,
    findingEntity,
    findingPlanumEntity,
    findingProfileEntity,
    findEntity,
    sampleEntity,
    overallDescriptionEntity,
    areaDescriptionEntity,
    diaryEntity,
    timeEntryEntity,
    photoEntity,
    drawingSheetEntity,
    mediaGroupEntity,
    glossaryTermEntity,
    billingCatalogEntity,
    billingPositionEntity,
    billingStatementEntity,
    billingStatementLineEntity,
    projectSchema,
    parcelSchema,
    userSchema,
    groupSchema,
    projectGroupSchema,
    userProfileSchema,
    findingSchema,
    findingPlanumSchema,
    findingProfileSchema,
    mediaSchema,
    mediaEntity,
    mediaLinkSchema,
    translationSchema,
    glossaryTermSchema,
    billingCatalogSchema,
    billingPositionSchema,
    billingStatementSchema,
    billingStatementLineSchema,
    mediaGroupSchema,
    findSchema,
    sampleSchema,
    overallDescriptionSchema,
    areaDescriptionSchema,
    diarySchema,
    timeEntrySchema,
    photoSchema,
    drawingSheetSchema,
    type ProjectDocType,
    type ParcelDocType,
    type UserDocType,
    type GroupDocType,
    type ProjectGroupDocType,
    type UserProfileDocType,
    type FindingDocType,
    type FindingPlanumDocType,
    type FindingProfileDocType,
    type MediaDocType,
    type MediaLinkDocType,
    type TranslationDocType,
    type GlossaryTermDocType,
    type BillingCatalogDocType,
    type BillingPositionDocType,
    type BillingStatementDocType,
    type BillingStatementLineDocType,
    type MediaGroupDocType,
    type FindDocType,
    type SampleDocType,
    type OverallDescriptionDocType,
    type AreaDescriptionDocType,
    type DiaryDocType,
    type TimeEntryDocType,
    type PhotoDocType,
    type DrawingSheetDocType
} from '@ast-data/shared';
import {
    projectConflictHandler,
    parcelConflictHandler,
    userConflictHandler,
    groupConflictHandler,
    projectGroupConflictHandler,
    userProfileConflictHandler,
    findingConflictHandler,
    findingPlanumConflictHandler,
    findingProfileConflictHandler,
    mediaConflictHandler,
    mediaLinkConflictHandler,
    translationConflictHandler,
    glossaryTermConflictHandler,
    mediaGroupConflictHandler,
    findConflictHandler,
    sampleConflictHandler,
    overallDescriptionConflictHandler,
    areaDescriptionConflictHandler,
    diaryConflictHandler,
    timeEntryConflictHandler,
    photoConflictHandler,
    drawingSheetConflictHandler,
    billingCatalogConflictHandler,
    billingPositionConflictHandler,
    billingStatementConflictHandler,
    billingStatementLineConflictHandler
} from '../features/sync/conflictHandler';
import { RxDBLeaderElectionPlugin } from 'rxdb/plugins/leader-election';
import { markBoot } from '../utils/bootTrace';
import {
    beginOpenProfile,
    endOpenProfile,
    profiledDexieSettings,
    RxDBOpenProfilePlugin,
    shareDatabaseListing
} from './openProfile';
import {
    beginOpenSteps,
    endOpenSteps,
    profiledHashFunction,
    profiledStorage
} from './openSteps';
import { setDatabaseOpening } from './openInFlight';
import { watchMigrations } from './migrationProgress';

// Required because the schemas declare migrationStrategies.
addRxPlugin(RxDBMigrationSchemaPlugin);

/**
 * Several tabs of this app share ONE database (`multiInstance` is RxDB's
 * default and stays on: it is what lets a write in one window appear in the
 * other). Exactly one of them may replicate, or two clients would push the same
 * documents and move the same checkpoint past each other — so the tabs elect a
 * leader, and the replication starts only there. `replicateRxCollection` adds
 * this plugin itself; naming it here is what lets the app ASK (see
 * `db.waitForLeadership()` in replication.ts) instead of finding out by
 * accident.
 */
addRxPlugin(RxDBLeaderElectionPlugin);

// Times every collection's open for the fault reports (db/openProfile).
addRxPlugin(RxDBOpenProfilePlugin);

export type ProjectCollection = RxCollection<ProjectDocType>;
export type ParcelCollection = RxCollection<ParcelDocType>;
export type UserCollection = RxCollection<UserDocType>;
export type GroupCollection = RxCollection<GroupDocType>;
export type ProjectGroupCollection = RxCollection<ProjectGroupDocType>;
export type UserProfileCollection = RxCollection<UserProfileDocType>;
export type FindingCollection = RxCollection<FindingDocType>;
export type FindingPlanumCollection = RxCollection<FindingPlanumDocType>;
export type FindingProfileCollection = RxCollection<FindingProfileDocType>;
export type MediaCollection = RxCollection<MediaDocType>;
export type MediaLinkCollection = RxCollection<MediaLinkDocType>;
export type TranslationCollection = RxCollection<TranslationDocType>;
export type GlossaryTermCollection = RxCollection<GlossaryTermDocType>;
export type BillingCatalogCollection = RxCollection<BillingCatalogDocType>;
export type BillingPositionCollection = RxCollection<BillingPositionDocType>;
export type BillingStatementCollection = RxCollection<BillingStatementDocType>;
export type BillingStatementLineCollection =
    RxCollection<BillingStatementLineDocType>;
export type MediaGroupCollection = RxCollection<MediaGroupDocType>;
export type FindCollection = RxCollection<FindDocType>;
export type SampleCollection = RxCollection<SampleDocType>;
export type OverallDescriptionCollection =
    RxCollection<OverallDescriptionDocType>;
export type AreaDescriptionCollection = RxCollection<AreaDescriptionDocType>;
export type DiaryCollection = RxCollection<DiaryDocType>;
export type TimeEntryCollection = RxCollection<TimeEntryDocType>;
export type PhotoCollection = RxCollection<PhotoDocType>;
export type DrawingSheetCollection = RxCollection<DrawingSheetDocType>;

export type AppCollections = {
    projects: ProjectCollection;
    parcels: ParcelCollection;
    users: UserCollection;
    groups: GroupCollection;
    project_groups: ProjectGroupCollection;
    user_profiles: UserProfileCollection;
    findings: FindingCollection;
    finding_planums: FindingPlanumCollection;
    finding_profiles: FindingProfileCollection;
    media: MediaCollection;
    media_links: MediaLinkCollection;
    media_groups: MediaGroupCollection;
    finds: FindCollection;
    samples: SampleCollection;
    overall_descriptions: OverallDescriptionCollection;
    area_descriptions: AreaDescriptionCollection;
    diaries: DiaryCollection;
    timeEntries: TimeEntryCollection;
    photos: PhotoCollection;
    drawing_sheets: DrawingSheetCollection;
    translations: TranslationCollection;
    glossary_terms: GlossaryTermCollection;
    billing_catalogs: BillingCatalogCollection;
    billing_positions: BillingPositionCollection;
    billing_statements: BillingStatementCollection;
    billing_statement_lines: BillingStatementLineCollection;
};

export type AppDatabase = RxDatabase<AppCollections>;

/**
 * Every collection with its schema, conflict handler and migrations. The
 * component tests build their in-memory database from the same map.
 */
export const APP_COLLECTIONS = {
    projects: {
        schema: projectSchema,
        conflictHandler: projectConflictHandler,
        migrationStrategies: projectEntity.migrations
    },
    parcels: {
        schema: parcelSchema,
        conflictHandler: parcelConflictHandler
    },
    users: {
        schema: userSchema,
        conflictHandler: userConflictHandler,
        migrationStrategies: userEntity.migrations
    },
    groups: {
        schema: groupSchema,
        conflictHandler: groupConflictHandler,
        migrationStrategies: groupEntity.migrations
    },
    project_groups: {
        schema: projectGroupSchema,
        conflictHandler: projectGroupConflictHandler
    },
    user_profiles: {
        schema: userProfileSchema,
        conflictHandler: userProfileConflictHandler,
        migrationStrategies: userProfileEntity.migrations
    },
    findings: {
        schema: findingSchema,
        conflictHandler: findingConflictHandler,
        migrationStrategies: findingEntity.migrations
    },
    finding_planums: {
        schema: findingPlanumSchema,
        conflictHandler: findingPlanumConflictHandler,
        migrationStrategies: findingPlanumEntity.migrations
    },
    finding_profiles: {
        schema: findingProfileSchema,
        conflictHandler: findingProfileConflictHandler,
        migrationStrategies: findingProfileEntity.migrations
    },
    media: {
        schema: mediaSchema,
        conflictHandler: mediaConflictHandler,
        migrationStrategies: mediaEntity.migrations
    },
    media_links: {
        schema: mediaLinkSchema,
        conflictHandler: mediaLinkConflictHandler
    },
    media_groups: {
        schema: mediaGroupSchema,
        conflictHandler: mediaGroupConflictHandler,
        migrationStrategies: mediaGroupEntity.migrations
    },
    finds: {
        schema: findSchema,
        conflictHandler: findConflictHandler,
        migrationStrategies: findEntity.migrations
    },
    samples: {
        schema: sampleSchema,
        conflictHandler: sampleConflictHandler,
        migrationStrategies: sampleEntity.migrations
    },
    overall_descriptions: {
        schema: overallDescriptionSchema,
        conflictHandler: overallDescriptionConflictHandler,
        migrationStrategies: overallDescriptionEntity.migrations
    },
    area_descriptions: {
        schema: areaDescriptionSchema,
        conflictHandler: areaDescriptionConflictHandler,
        migrationStrategies: areaDescriptionEntity.migrations
    },
    diaries: {
        schema: diarySchema,
        conflictHandler: diaryConflictHandler,
        migrationStrategies: diaryEntity.migrations
    },
    timeEntries: {
        schema: timeEntrySchema,
        conflictHandler: timeEntryConflictHandler,
        migrationStrategies: timeEntryEntity.migrations
    },
    photos: {
        schema: photoSchema,
        conflictHandler: photoConflictHandler,
        migrationStrategies: photoEntity.migrations
    },
    drawing_sheets: {
        schema: drawingSheetSchema,
        conflictHandler: drawingSheetConflictHandler,
        migrationStrategies: drawingSheetEntity.migrations
    },
    translations: {
        schema: translationSchema,
        conflictHandler: translationConflictHandler
    },
    glossary_terms: {
        schema: glossaryTermSchema,
        conflictHandler: glossaryTermConflictHandler,
        migrationStrategies: glossaryTermEntity.migrations
    },
    billing_catalogs: {
        schema: billingCatalogSchema,
        conflictHandler: billingCatalogConflictHandler,
        migrationStrategies: billingCatalogEntity.migrations
    },
    billing_positions: {
        schema: billingPositionSchema,
        conflictHandler: billingPositionConflictHandler,
        migrationStrategies: billingPositionEntity.migrations
    },
    billing_statements: {
        schema: billingStatementSchema,
        conflictHandler: billingStatementConflictHandler,
        migrationStrategies: billingStatementEntity.migrations
    },
    billing_statement_lines: {
        schema: billingStatementLineSchema,
        conflictHandler: billingStatementLineConflictHandler,
        migrationStrategies: billingStatementLineEntity.migrations
    }
} satisfies Record<keyof AppCollections, unknown>;

let dbPromise: Promise<AppDatabase> | null = null;

export function getDatabase(): Promise<AppDatabase> {
    if (!dbPromise) {
        // Development only, and compiled away in a build: `?fault=db-fail` or
        // `?fault=db-stall` stands in for an open that cannot otherwise be
        // brought about on purpose (features/support/devFaults).
        dbPromise = injectedDatabaseFault() ?? createDatabase();
    }
    return dbPromise;
}

/** After this long an open is no ordinary start any more — it is a migration. */
const LONG_OPEN_MS = 3000;

/**
 * Guard the one window in which this app can break itself.
 *
 * A document that goes away WHILE the database is opening leaves a connection
 * behind that no later document gets past: the next start — same tab, another
 * tab, after a reload, even after a wipe — waits on that database name forever,
 * and only closing the window releases it (see db/openStall.ts). The window is
 * a fifth of a second on a warm start and minutes during a migration, which is
 * why this keeps coming back after a schema bump and never in between.
 *
 * Two things happen here, and neither can be done from a React effect: by the
 * time a component has a database to hang a listener on, the dangerous window
 * is already over.
 *
 * 1. Leaving during the open is remembered, so the NEXT start can name the
 *    cause instead of blaming a slow device.
 * 2. Once the open is slow enough to be a migration, the browser asks before
 *    leaving. A normal start never gets this far; a migration is precisely
 *    where a reload costs the person their app until they close the window.
 */
let dropOpenGuard: () => void = () => {};

/**
 * Let go of the guard for a reload the APP decided on.
 *
 * The recovery screens reload on purpose while the open is still hanging — the
 * one case where `createDatabase` never reaches its `finally`. Without this,
 * their buttons would raise the browser's own "leave site?" dialog on top of a
 * recovery screen, and the pagehide handler would blame the next start for an
 * interruption that was our own idea.
 */
export function releaseOpenGuard(): void {
    dropOpenGuard();
}

function guardOpenInFlight(): () => void {
    /**
     * Every way out counts, the bfcache included — measured: leaving mid-open
     * reports `persisted: true`, and that frozen document is exactly what holds
     * the database. Skipping it here cost nineteen seconds of spinner before
     * the next start said anything.
     *
     * A page that really does come back carries no cost: the open runs on, and
     * finishing it clears the note.
     */
    const onPageHide = () => markOpenAbandoned();
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
        event.preventDefault();
    };
    window.addEventListener('pagehide', onPageHide);
    const askBeforeLeaving = window.setTimeout(
        () => window.addEventListener('beforeunload', onBeforeUnload),
        LONG_OPEN_MS
    );
    dropOpenGuard = () => {
        window.clearTimeout(askBeforeLeaving);
        window.removeEventListener('pagehide', onPageHide);
        window.removeEventListener('beforeunload', onBeforeUnload);
        dropOpenGuard = () => {};
    };
    return () => {
        dropOpenGuard();
        clearOpenAbandoned();
    };
}

async function createDatabase(): Promise<AppDatabase> {
    /**
     * The three marks below are what separate the start-up stalls that look
     * identical from the outside: `db:created` missing means IndexedDB never
     * handed the database over (another window holding the old version blocks
     * exactly here); `db:collections` missing means a schema migration is what
     * is running. See utils/bootTrace.
     */
    markBoot('db:opening');
    beginOpenProfile();
    beginOpenSteps();
    // Dexie's Safari workaround polls this once per collection; on an iPhone
    // that queue of calls is where the start-up time went (db/openProfile).
    const stopSharingListing = shareDatabaseListing();
    setDatabaseOpening(true);
    const releaseGuard = guardOpenInFlight();
    try {
        return await openDatabase();
    } finally {
        releaseGuard();
        stopSharingListing();
        endOpenProfile();
        endOpenSteps();
        setDatabaseOpening(false);
    }
}

async function openDatabase(): Promise<AppDatabase> {
    const db = await createRxDatabase<AppCollections>({
        // Not a constant: a copy a wedged connection holds is given up on and
        // the next generation opened instead (db/openStall.ts).
        name: currentDatabaseName(),
        storage: profiledStorage(getRxStorageDexie(profiledDexieSettings)),
        // RxDB's default, only counted — the hashes are stored and compared.
        hashFunction: profiledHashFunction
    });
    markBoot('db:created');

    const stopWatchingMigrations = watchMigrations(db);
    try {
        await db.addCollections(APP_COLLECTIONS);
    } finally {
        stopWatchingMigrations();
    }

    markBoot('db:collections');

    // Must happen before anything can write: the journal is what lets a
    // handed-over device still push the previous user's edits as them.
    installAuthorJournal(db);

    return db;
}
